import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link2, RefreshCw } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { censurarTexto, email } from '../../nucleo/formato';

/** U14 F6: os apps da Maton que o MalgoOS usa (ação: Google Sheets e HubSpot; evento: HubSpot e Gmail). */
export const APPS_MATON = ['hubspot', 'google-sheets', 'google-mail'] as const;

export interface ConexaoMaton {
  id: string;
  app: string;
  estado: 'ACTIVE' | 'PENDING' | 'FAILED';
  conta: string | null;
  criada_em: string | null;
}

const COR: Record<ConexaoMaton['estado'], CorSelo> = {
  ACTIVE: 'verde',
  PENDING: 'laranja',
  FAILED: 'perigo',
};
export const chaveConexoesMaton = (app?: string) => ['integracoes', 'maton', 'conexoes', app ?? 'todas'] as const;

/** A conta da conexão no modo discreto (e-mail pelo formatador; nome de equipe, como texto). */
export const contaMaton = (conta: string | null) => (conta ? (conta.includes('@') ? email(conta) : censurarTexto(conta)) : '');

/**
 * U14 F6: conexões da Maton no cartão (as contas dos apps ficam na Maton). "Conectar app" cria a conexão e abre o link de
 * autorização (30 minutos) numa aba nova; "Atualizar" relê a lista depois que a pessoa autoriza.
 */
export function ConexoesMaton({ pode }: { pode: boolean }) {
  const { t } = useTranslation('integracoes');
  const toast = useToast();
  const qc = useQueryClient();
  const [app, setApp] = useState<string>('hubspot');
  const q = useQuery({
    queryKey: chaveConexoesMaton(),
    queryFn: () => get<{ itens: ConexaoMaton[] }>('/integracoes/conectores/maton/conexoes'),
  });
  const conectar = useMutation({
    mutationFn: async () => {
      // A aba abre no clique (o navegador bloqueia a que abre depois da resposta) e recebe o link quando ele chega.
      const aba = window.open('about:blank', '_blank');
      try {
        const r = await post<{ conexao_id: string; url: string }>('/integracoes/conectores/maton/conexoes', { app });
        if (!aba) throw new Error(t('conectores.maton.bloqueado'));
        aba.opener = null;
        aba.location.href = r.url;
        return r;
      } catch (e) {
        aba?.close();
        throw e;
      }
    },
    onSuccess: async () => {
      toast.sucesso(t('conectores.maton.aberto'));
      await qc.invalidateQueries({
        queryKey: ['integracoes', 'maton', 'conexoes'],
      });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const itens = q.data?.itens ?? [];

  return (
    <div className="flex flex-col gap-2" data-conexoes-maton>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-tinta-3">{t('conectores.maton.conexoes')}</span>
        <Botao tamanho="sm" icone={<RefreshCw className="size-4" aria-hidden />} carregando={q.isFetching} onClick={() => void q.refetch()}>
          {t('conectores.maton.atualizar')}
        </Botao>
      </div>
      {q.isError && <p className="text-xs text-perigo">{mensagemDeErro(q.error)}</p>}
      {!q.isError && !q.isPending && !itens.length && <p className="text-xs text-tinta-3">{t('conectores.maton.vazio')}</p>}
      {itens.length > 0 && (
        <ul className="flex flex-col gap-1.5 text-xs">
          {itens.map((c) => (
            <li key={c.id} className="flex min-w-0 items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 font-medium text-tinta-2">{t(`conectores.maton.apps.${c.app}`, { defaultValue: c.app })}</span>
                {c.conta && <TextoCortado className="min-w-0 text-tinta-3">{contaMaton(c.conta)}</TextoCortado>}
              </span>
              <Selo cor={COR[c.estado]}>{t(`conectores.maton.estados.${c.estado}`)}</Selo>
            </li>
          ))}
        </ul>
      )}
      {pode && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-40 flex-1">
            <Rotulo id="maton-app" rotulo={t('conectores.maton.app')}>
              <Selecao
                id="maton-app"
                value={app}
                onChange={(e) => setApp(e.target.value)}
                opcoes={APPS_MATON.map((a) => ({
                  valor: a,
                  rotulo: t(`conectores.maton.apps.${a}`),
                }))}
              />
            </Rotulo>
          </div>
          <Botao icone={<Link2 className="size-4" aria-hidden />} carregando={conectar.isPending} onClick={() => conectar.mutate()}>
            {t('conectores.maton.conectar')}
          </Botao>
        </div>
      )}
    </div>
  );
}
